// /en/visa-compliance/$topic — EN detail page (Phase P-TM30-90DAY)
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import type { ComplianceTopic } from "@/data/visa-compliance-topics";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, AlertTriangle, FileText, Clock, Wallet, ShieldCheck, ChevronRight } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/visa-compliance/$topic")({
  loader: async ({ params }) => {
    const { COMPLIANCE_TOPICS } = await import("@/data/visa-compliance-topics");
    const topic = COMPLIANCE_TOPICS.find((t) => t.slug === params.topic);
    if (!topic) throw notFound();
    return {
      __d: { COMPLIANCE_TOPICS }, topic };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const t = loaderData.topic;
    const url = `${SITE}/en/visa-compliance/${t.slug}`;
    const title = `${t.titleEn} — Steps, Fees, Deadlines 2026 | NYC Thailand`;
    const desc = `${t.quickAnswerEn.slice(0, 155)} — Talk to a specialist today.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/visa-compliance/$topic", "en") }, { name: "description", content: ctaDesc(desc, "/en/visa-compliance/$topic", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/visa-compliance/$topic", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/visa-compliance/$topic", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/visa-compliance/${t.slug}`, `/en/visa-compliance/${t.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: t.titleEn, serviceType: t.titleEn, description: desc,
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: String(t.servicePriceTHB), priceCurrency: "THB", description: `${t.titleEn} service` },
            }),
          },
          howToSchema({
            name: `How to: ${t.titleEn}`,
            description: t.taglineEn,
            totalTime: "P1D",
            estimatedCost: { currency: "THB", value: String(t.servicePriceTHB) },
            inLanguage: "en",
            steps: t.stepsEn,
          }),
          faqPageSchema(t.faqsEn),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Visa Compliance", item: `${SITE}/en/visa-compliance` },
                { "@type": "ListItem", position: 3, name: t.titleEn, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">Topic not found</h1>
      <Link to="/en/visa-compliance" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">Back to hub</Link>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-bold">Something went wrong</h1>
      <Link to="/en/visa-compliance" className="mt-4 inline-block text-primary">Back to hub</Link>
    </main>
  ),
  component: ComplianceTopicEn,
});

function ComplianceTopicEn() {
  const { __d: { COMPLIANCE_TOPICS } } = Route.useLoaderData() as { __d: typeof import("@/data/visa-compliance-topics") };
  const { topic: t } = Route.useLoaderData() as { topic: ComplianceTopic };
  const others = COMPLIANCE_TOPICS.filter((x) => x.slug !== t.slug).slice(0, 6);
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <Link to="/en/visa-compliance" className="hover:text-primary">Visa Compliance</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <span>{t.titleEn}</span>
          </nav>
          <div className="text-4xl">{t.icon}</div>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">{t.titleEn}</h1>
          <p className="mt-3 max-w-3xl text-lg text-muted-foreground">{t.taglineEn}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <p className="text-sm leading-relaxed text-foreground sm:text-base">{t.quickAnswerEn}</p>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3">
              <Wallet className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">Government fee</div>
              <div className="text-sm font-bold">{t.feeNoteEn}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Clock className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">Deadline</div>
              <div className="text-sm font-bold">{t.deadlineEn}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <FileText className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">Forms</div>
              <div className="text-sm font-bold">{t.forms.join(" · ")}</div>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92 242 9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-extrabold">Step-by-step process ({t.stepsEn.length} steps)</h2>
              <ol className="mt-4 space-y-3">
                {t.stepsEn.map((s, i) => (
                  <li key={i} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{i + 1}</div>
                      <div>
                        <div className="font-bold">{s.name}</div>
                        <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">Frequently asked questions</h2>
              <div className="mt-4 space-y-3">
                {t.faqsEn.map((f, i) => (
                  <details key={i} className="rounded-xl border border-border bg-card p-4">
                    <summary className="cursor-pointer font-bold">{f.q}</summary>
                    <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold">Who must do this</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t.whoEn}</p>
            </div>
            <div className="rounded-2xl border-2 border-amber-500/40 bg-amber-500/5 p-5">
              <h3 className="flex items-center gap-2 font-bold text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" /> Penalty / impact
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">{t.penaltyEn}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold">Legal reference</h3>
              <p className="mt-2 text-xs text-muted-foreground">{t.lawRef}</p>
            </div>
            <div className="rounded-2xl border border-primary bg-primary/5 p-5">
              <h3 className="font-bold">NYC service</h3>
              <p className="mt-2 text-2xl font-extrabold text-primary">THB {t.servicePriceTHB.toLocaleString()}</p>
              <p className="mt-1 text-xs text-muted-foreground">Documents + Immigration escort + verification included</p>
              <a href="tel:+66922429293" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">
                <Phone className="h-4 w-4" /> Call us
              </a>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold"><ShieldCheck className="mr-2 inline h-5 w-5 text-primary" />Other compliance topics</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to="/en/visa-compliance/$topic" params={{ topic: o.slug }} className="rounded-xl border border-border bg-card p-3 hover:border-primary">
                <div className="flex items-center gap-2"><span className="text-xl">{o.icon}</span><span className="font-bold text-sm">{o.titleEn}</span></div>
                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{o.taglineEn}</div>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">View <ArrowRight className="h-3 w-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    </main>
  );
}
